import { Copy, Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toTotpCode } from '../api/mappers';
import {
  useEnableTwoFactor,
  useRefreshTwoFactor,
  useSetUpTwoFactor,
} from '../hooks/use-two-factor-mutations';
import { twoFactorFieldErrors } from '../schemas/two-factor-rules';
import { authenticatorCodeSchema, type CodeValues } from '../schemas/two-factor-schemas';

import { OneTimeCodeInput } from './one-time-code-input';
import { QrCode } from './qr-code';
import { RecoveryCodesPanel } from './recovery-codes-panel';

/** `JBSW Y3DP EHPK 3PXP`: easier to type into an app than one long run. */
function groupSecret(secret: string): string {
  return secret.replace(/(.{4})(?=.)/g, '$1 ');
}

export interface SetUpTwoFactorDialogProps {
  account: string;
  /** A secret was generated earlier but never confirmed. */
  resume?: boolean;
}

/**
 * Setup → scan or type the secret → confirm with a code → recovery codes. Opening the dialog
 * asks for a fresh secret; the previous unconfirmed one is replaced by the backend.
 */
export function SetUpTwoFactorDialog({ account, resume = false }: SetUpTwoFactorDialogProps) {
  const { t } = useTranslation('auth');
  const message = useErrorMessage();
  const setup = useSetUpTwoFactor();
  const enable = useEnableTwoFactor();
  const refresh = useRefreshTwoFactor();
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const form = useForm<CodeValues>({
    resolver: useI18nResolver(authenticatorCodeSchema),
    defaultValues: { code: '' },
  });

  const onOpenChange = (next: boolean) => {
    // Once the codes are on screen, only "Done" (after confirming they were saved) closes.
    if (!next && codes) return;
    setOpen(next);
    if (next) {
      setup.mutate();
    } else {
      form.reset();
      setup.reset();
      enable.reset();
    }
  };

  const finish = () => {
    setCodes(null);
    setOpen(false);
    form.reset();
    setup.reset();
    enable.reset();
    toast.success(t('security.enabledToast'));
    void refresh();
  };

  const copySecret = async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      toast.success(t('setup.secretCopied'));
    } catch {
      toast.error(t('recoveryCodes.copyFailed'));
    }
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const result = await enable.mutateAsync({ code: toTotpCode(values.code) });
      setCodes(result.recoveryCodes);
    } catch (error) {
      if (twoFactorFieldErrors(error).includes('code')) {
        form.setError(
          'code',
          { type: 'server', message: t('setup.codeMismatch') },
          { shouldFocus: true },
        );
      } else {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  const rootError = form.formState.errors.root?.server?.message;
  const data = setup.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <ShieldCheck aria-hidden />
          {resume ? t('security.resumeSetup') : t('security.setUp')}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg"
        showCloseButton={!codes}
      >
        <DialogHeader>
          <DialogTitle>{codes ? t('recoveryCodes.title') : t('setup.title')}</DialogTitle>
          <DialogDescription>
            {codes ? t('recoveryCodes.description') : t('setup.description')}
          </DialogDescription>
        </DialogHeader>
        {codes ? (
          <RecoveryCodesPanel codes={codes} account={account} onDone={finish} />
        ) : setup.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(setup.error)}</AlertDescription>
          </Alert>
        ) : !data ? (
          <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {t('setup.generating')}
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            <ol className="text-muted-foreground list-decimal space-y-1 pl-5 text-sm">
              <li>{t('setup.stepScan')}</li>
              <li>{t('setup.stepCode')}</li>
            </ol>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
              <QrCode value={data.otpauthUrl} label={t('setup.qrLabel')} />
              <div className="flex min-w-0 flex-col gap-2 text-sm">
                <span className="text-muted-foreground">{t('setup.cantScan')}</span>
                <code
                  className="bg-muted rounded-md px-3 py-2 font-mono text-sm break-all select-all"
                  data-testid="two-factor-secret"
                >
                  {groupSecret(data.secret)}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => void copySecret(data.secret)}
                >
                  <Copy aria-hidden />
                  {t('setup.copySecret')}
                </Button>
              </div>
            </div>
            <Form {...form}>
              <form onSubmit={submit} noValidate className="flex flex-col gap-4">
                {rootError ? (
                  <Alert variant="destructive" role="alert">
                    <AlertDescription>{rootError}</AlertDescription>
                  </Alert>
                ) : null}
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('twoFactor.label.totp')}</FormLabel>
                      <FormControl>
                        <OneTimeCodeInput {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="submit" disabled={enable.isPending}>
                    {enable.isPending ? t('setup.enabling') : t('setup.enable')}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
